import { useRef, useState, useEffect, useCallback, useId } from 'react';
import { Trash2 } from 'lucide-react';
import styles from './SignaturePad.module.css';

interface SignaturePadProps {
  value: string | null;
  onChange: (dataUri: string | null) => void;
  label?: string;
  error?: { message?: string };
  disabled?: boolean;
}

type ActiveTab = 'draw' | 'type';

// Canvas stroke color — CSS vars are not readable by canvas context,
// so we use a fixed dark value that matches --color-text-primary.
const STROKE_COLOR = '#0f172a';
const LINE_WIDTH = 2;

// Signature font for the Type tab.
const SIGNATURE_FONT = 'italic 48px "Brush Script MT", "Segoe Script", cursive';

export function SignaturePad({
  value,
  onChange,
  label,
  error,
  disabled,
}: SignaturePadProps): JSX.Element {
  const panelId = useId();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('draw');
  const [typedName, setTypedName] = useState('');
  const isDrawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  // When switching to draw tab, restore any existing signature value.
  useEffect(() => {
    if (activeTab !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (value?.startsWith('data:image/png')) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
    }
  }, [activeTab, value]);

  function canvasPoint(
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  ) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = 'touches' in e ? e.touches[0] : e;
    if (!src) return { x: 0, y: 0 };
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  }

  function startDraw(
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  ) {
    e.preventDefault();
    if (disabled) return;
    isDrawing.current = true;
    lastPoint.current = canvasPoint(e);
  }

  function continueDraw(
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  ) {
    e.preventDefault();
    if (!isDrawing.current || disabled) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const pt = canvasPoint(e);
    ctx.beginPath();
    ctx.strokeStyle = STROKE_COLOR;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.moveTo(lastPoint.current!.x, lastPoint.current!.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPoint.current = pt;
  }

  function endDraw() {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    lastPoint.current = null;
    onChange(canvasRef.current?.toDataURL('image/png') ?? null);
  }

  function clearCanvas() {
    const canvas = canvasRef.current!;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  }

  const generateTypeSignature = useCallback(
    (name: string) => {
      if (!name.trim()) {
        onChange(null);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 160;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = SIGNATURE_FONT;
      ctx.fillStyle = STROKE_COLOR;
      ctx.textBaseline = 'middle';
      ctx.fillText(name, 16, 80);
      onChange(canvas.toDataURL('image/png'));
    },
    [onChange],
  );

  function switchTab(tab: ActiveTab) {
    if (tab === 'draw') {
      // Clearing typed signature when going back to draw
      setTypedName('');
    }
    setActiveTab(tab);
    if (tab !== 'draw') onChange(null);
  }

  return (
    <div className={styles.root}>
      {label && <span className={styles.label}>{label}</span>}

      <div className={styles.tabs} role="tablist">
        {(['draw', 'type'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            type="button"
            aria-selected={activeTab === tab}
            aria-controls={`${panelId}-${tab}`}
            className={activeTab === tab ? styles.tabActive : styles.tab}
            onClick={() => switchTab(tab)}
            disabled={disabled}
          >
            {tab === 'draw' ? 'Draw' : 'Type'}
          </button>
        ))}
      </div>

      <div id={`${panelId}-draw`} role="tabpanel" hidden={activeTab !== 'draw'}>
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          className={`${styles.canvas} ${disabled ? styles.canvasDisabled : ''}`}
          aria-label="Signature drawing area"
          onMouseDown={startDraw}
          onMouseMove={continueDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={continueDraw}
          onTouchEnd={endDraw}
        />
        {!disabled && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={clearCanvas}
            aria-label="Clear signature"
          >
            <Trash2 size={13} />
            Clear
          </button>
        )}
      </div>

      <div id={`${panelId}-type`} role="tabpanel" hidden={activeTab !== 'type'}>
        <input
          type="text"
          className={styles.typeInput}
          placeholder="Type your full name"
          value={typedName}
          disabled={disabled}
          onChange={(e) => {
            setTypedName(e.target.value);
            generateTypeSignature(e.target.value);
          }}
          aria-label="Type name for signature"
        />
        {value && activeTab === 'type' && (
          <div className={styles.typePreview}>
            <img src={value} alt="Signature preview" className={styles.typePreviewImg} />
          </div>
        )}
      </div>

      {value && (
        <p className={styles.captured}>Signature captured</p>
      )}

      {error?.message && (
        <p className={styles.errorMsg} role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
}
